"use client";

import { Button } from "@/components/ui/button";
import { ArrowRight, Check, LockKeyhole } from "lucide-react";
import Image from "next/image";

interface CustomWheelUploadProps {
  onTryItNow: () => void;
}

export function CustomWheelUpload({ onTryItNow }: CustomWheelUploadProps) {
  return (
    <div className="mx-auto max-w-7xl">
        <div className="grid items-end gap-6 md:grid-cols-[1fr_auto]">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/35">
              Bring any wheel
            </p>
            <h2 className="mt-4 max-w-2xl text-4xl font-bold tracking-tight text-white md:text-6xl">
              Don&apos;t see your wheel? Upload it.
          </h2>
          </div>
          <p className="max-w-sm text-base leading-relaxed text-white/50 md:pb-2 md:text-right">
            Use a clear product image, phone photo, or listing screenshot to
            preview a wheel that is not in the catalog.
          </p>
        </div>

        <div className="mt-12 rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/[0.055] to-white/[0.02] p-3 shadow-2xl shadow-black/20 md:p-5">
          <div className="grid gap-3 md:grid-cols-3">
            {[
              {
                title: "Product photos",
                description: "From shops or manufacturers",
                image: "/showcase/custom/upload3.webp",
                label: "Cleanest source",
              },
              {
                title: "Phone photos",
                description: "Of real wheels on cars",
                image: "/showcase/custom/upload2.webp",
                label: "Works in a pinch",
              },
              {
                title: "Listing images",
                description: "From websites or marketplaces",
                image: "/showcase/custom/upload1.webp",
                label: "Easy to find",
              },
            ].map((item, idx) => {
              return (
                <article
                  key={idx}
                  className="group relative rounded-[1.4rem] border border-white/[0.07] bg-[#0D1014] p-4"
                >
                    <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl bg-black/20">
                      <Image
                        src={item.image}
                        alt={item.title}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                        sizes="(max-width: 768px) 50vw, 25vw"
                      />
                      <span className="absolute left-3 top-3 rounded-full border border-white/15 bg-black/60 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/70 backdrop-blur-sm">
                        Option 0{idx + 1}
                      </span>
                    </div>
                    <div className="px-2 pb-2 pt-5">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/30">
                        {item.label}
                      </p>
                      <h3 className="mt-2 text-xl font-semibold text-white">
                        {item.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-white/50">
                        {item.description}
                      </p>
                    </div>
                </article>
              );
            })}
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-5 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-6 py-5 sm:flex-row">
          <div>
            <p className="flex items-center justify-center gap-2 text-sm font-medium text-white/65 sm:justify-start">
              <Check className="h-4 w-4 text-white" aria-hidden />
              Clear, well-lit images work best
            </p>
            <div className="mt-2 flex items-start gap-2 text-center text-xs leading-relaxed text-white/35 sm:text-left">
              <LockKeyhole className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              <p>
                Original uploads are not published. Selected free-preview
                results may appear in our public gallery.
              </p>
            </div>
          </div>
          <div className="w-full shrink-0 text-center sm:w-auto sm:text-right">
            <Button
              onClick={onTryItNow}
              size="lg"
              className="group h-12 w-full bg-white px-7 text-base text-black hover:bg-white/90 sm:w-auto"
            >
              Explore paid custom-wheel uploads
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-0.5" />
            </Button>
            <p className="mt-2 text-xs text-white/35">
              Included with every paid pack.
            </p>
          </div>
        </div>
    </div>
  );
}
